import { useState } from 'react';

import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  useDeleteMisclassifiedCall,
  useMisclassifiedCallList,
} from '@/hooks/useMisclassifiedCalls';
import { usePageTitle } from '@/hooks/usePageTitle';

export const MisclassifiedCallsListPage = () => {
  usePageTitle('Misclassified Calls');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: calls, isLoading } = useMisclassifiedCallList();
  const deleteMutation = useDeleteMisclassifiedCall();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast({ variant: 'success', title: 'Removed', description: 'Call removed from registry.' });
      setDeleteTarget(null);
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to remove.' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Misclassified Calls</CardTitle>
            <CardDescription>
              A registry of calls the classifier got wrong, each with the correct classification and
              tags. Judge tuning selects from this pool by tag.
            </CardDescription>
          </div>
          <Button onClick={() => navigate('/misclassified-calls/new')}>
            <Plus className="mr-2 h-4 w-4" /> Add call
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Call</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(calls ?? []).map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono text-xs">{c.call_id.slice(0, 8)}…</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {c.target_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{c.call?.phone_number ?? '—'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {c.tags.length ? (
                        c.tags.map((t) => (
                          <Badge key={t} variant="secondary" className="text-xs">
                            {t}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-xs truncate text-xs">
                    {c.reason ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {new Date(c.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/misclassified-calls/${c.id}`)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteTarget({ id: c.id, label: c.call_id })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        resourceName="misclassified call"
        resourceIdentifier={deleteTarget?.label}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
